import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  ElementRef,
  effect,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {DirectoryType, UsersService, UserType, VerificationService} from '@on-the-go-web/sdk';
import {AuthService} from '@on-the-go-web/helpers/services/auth.service';
import {
  emailValidator,
  EventTrackService,
  LocationsDataService,
} from '@on-the-go-web/library/helpers';
import {Router, RouterModule, ActivatedRoute} from '@angular/router';
import {LOGO_PATH_TOKEN} from '@on-the-go-web/library';
import {OtpInputComponent} from '@on-the-go-web/library/components/otp-input/otp-input';
import {isPlatformBrowser} from '@angular/common';
import {interval, of, Subject, timer} from 'rxjs';
import {
  catchError,
  map,
  first,
  takeWhile,
  take,
  switchMap,
  filter,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'otg-login',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    OtpInputComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy, OnInit {
  private _usersService = inject(UsersService);
  private _verificationService = inject(VerificationService);
  private _auth = inject(AuthService);
  private _track = inject(EventTrackService);
  private _route = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _translate = inject(TranslateService);
  private _locationsData = inject(LocationsDataService);
  private _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private oauthWindow: Window | null = null;
  public logoPath$ = inject(LOGO_PATH_TOKEN);
  private stopPolling$ = new Subject<void>();

  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;
  @ViewChild(OtpInputComponent) otpInput?: OtpInputComponent;

  loginForm = new FormGroup({
    email: new FormControl<string>('', [emailValidator()]),
    password: new FormControl<string>(''),
  });

  hide = signal(true);
  error = signal<string | null>(null);
  loginTypeLoading = signal<boolean>(false);

  // Return URL for redirecting after login
  returnUrl = signal<string | null>(null);

  // OTP login mode
  loginMode = signal<'unknown' | 'password' | 'otp'>('unknown');
  showOtpInput = signal<boolean>(false);
  otpVerificationId = signal<string | null>(null);
  otpSending = signal<boolean>(false);
  otpVerifying = signal<boolean>(false);
  otpResendCountdown = signal<number>(0);
  detectedUserType = signal<UserType | null>(null);
  protected readonly UserType = UserType;

  constructor() {
    // Auto-focus logic
    effect(() => {
      if (this.loginMode() === 'password') {
        setTimeout(() => {
          this.passwordInput?.nativeElement.focus();
        }, 100);
      }
    });

    effect(() => {
      if (this.showOtpInput()) {
        setTimeout(() => {
          this.otpInput?.focus();
        }, 100);
      }
    });
  }

  resetLoginType() {
    this.loginMode.set('unknown');
    this.showOtpInput.set(false);
    this.otpVerificationId.set(null);
    this.error.set(null);
    this.detectedUserType.set(null);
  }

  switchToOtpLogin() {
    this.loginMode.set('otp');
    this.error.set(null);
    this.detectedUserType.set(null);
  }

  switchToPasswordLogin() {
    this.loginMode.set('password');
    this.error.set(null);
    this.showOtpInput.set(false);
    this.otpVerificationId.set(null);
  }

  ngOnInit(): void {
    // Get return URL from query params
    const returnUrl = this._activatedRoute.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.returnUrl.set(returnUrl);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    if (this._isBrowser) {
      window.removeEventListener('message', this.receiveMessage, false);
    }
  }

  private pollVerificationStatus(id: string) {
    timer(15000, 15000)
      .pipe(
        take(3),
        takeUntil(this.stopPolling$),
        takeWhile(() => this.showOtpInput()),
        switchMap(() => this._verificationService.getVerifyStatus(id)),
        filter((res: any) => res.status === 'FAILED'),
      )
      .subscribe(() => {
        this._track.trackEmailDeliveryFailed('login');
        this.error.set(
          this._translate.instant('emailBounceError') ||
            'Email delivery failed. Please check the address and try again.',
        );
        this.showOtpInput.set(false);
        this.otpVerifying.set(false);
        this.stopPolling$.next();
      });
  }

  onSubmit() {
    if (this.loginMode() === 'unknown') {
      this.checkLoginType();
    } else if (this.loginMode() === 'password') {
      this.login();
    } else if (this.loginMode() === 'otp' && this.showOtpInput()) {
      this.verifyLoginOtp(this.otpInput?.code() ?? '');
    }
  }

  checkLoginType() {
    if (!this.loginForm.valid) {
      this.loginForm.get('email')?.markAsTouched();
      return;
    }
    const email = this.loginForm.get('email')?.value;
    if (!email) {
      return;
    }

    this.loginTypeLoading.set(true);
    this._usersService
      .userTypePost({
        email,
      })
      .pipe(
        map((result) => {
          if (!result) {
            throw new Error('Result is null');
          }
          return result;
        }),
      )
      .subscribe({
        next: (result) => {
          this.loginTypeLoading.set(false);
          if (result.type === UserType.Yext) {
            this._track.trackAuthCheck('login', 'yext');
            this.initOauthFlow();
          } else if (result.type === null || result.type === undefined) {
            this.detectedUserType.set(null);
            this._track.trackAuthCheck('login', 'new');
            this.loginMode.set('otp');
            this.registerAndSendOtp(email);
          } else {
            this.detectedUserType.set(result.type ?? null);
            const mode = result.type === UserType.OtgPasswordless ? 'otp' : 'password';
            this._track.trackAuthCheck('login', mode);
            this.loginMode.set(mode);
          }
        },
        error: (error) => {
          this.loginTypeLoading.set(false);
          this.error.set(error?.error?.error || this._translate.instant('loginError'));
        },
      });
  }

  login() {
    if (this.loginForm.valid) {
      const email = this.loginForm.get('email')?.value;
      const password = this.loginForm.get('password')?.value;
      if (email && password) {
        this.error.set(null);
        this._usersService
          .userLoginPost({
            email,
            password,
          })
          .pipe(
            first(),
            tap((result) => {
              this._track.trackLogin('login', 'password', true);
              this._auth.setAuthData(result.access_token, result.userType);
            }),
            switchMap((response) => this._locationsData.fetchLocations$.pipe(map(() => response))),
          )
          .pipe(
            map((result) => {
              if (!result) {
                throw new Error('Result is null');
              }
              return result;
            }),
          )
          .subscribe({
            next: () => {
              this.onLoginSuccess();
            },
            error: (error) => {
              this._track.trackLogin(
                'login',
                'password',
                false,
                error?.error?.error ?? error?.status?.toString(),
              );
              this.error.set(error?.error?.error || this._translate.instant('loginError'));
            },
          });
      }
    }
  }

  private registerAndSendOtp(email: string): void {
    this._usersService
      .userRegisterPost({email, directory: DirectoryType.Pa})
      .pipe(
        first(),
        catchError(() => of(null)),
      )
      .subscribe(() => this.sendLoginOtp());
  }

  sendLoginOtp() {
    const email = this.loginForm.get('email')?.value;
    if (!email) return;

    this.otpSending.set(true);
    this.error.set(null);

    this._usersService
      .userOtpLoginPost({email})
      .pipe(first())
      .subscribe({
        next: (response) => {
          this._track.trackOtpSent('login', 'email', true);
          this.otpVerificationId.set(response.id || null);
          this.showOtpInput.set(true);
          this.otpSending.set(false);
          this.startResendCountdown();
          if (response.id) {
            this.pollVerificationStatus(response.id);
          }
        },
        error: (err) => {
          this._track.trackOtpSent(
            'login',
            'email',
            false,
            err?.error?.error ?? err?.status?.toString(),
          );
          this.error.set(err?.error?.error || this._translate.instant('failedToSendCode'));
          this.otpSending.set(false);
        },
      });
  }

  verifyLoginOtp(code: string) {
    const verificationId = this.otpVerificationId();
    if (!verificationId) return;

    this.otpVerifying.set(true);
    this.error.set(null);

    this._usersService
      .userOtpLoginCompletePost(
        {
          id: verificationId,
          code,
        },
        'response',
      )
      .pipe(
        first(),
        tap((response) => {
          if (response.body?.access_token) {
            this._track.trackOtpVerified('login', 'email', true);
            this._track.trackLogin('login', 'otp', true);
            this._auth.setAuthData(response.body?.access_token, UserType.OtgPasswordless);
          }
        }),
        switchMap((response) => this._locationsData.fetchLocations$.pipe(map(() => response))),
      )
      .subscribe({
        next: (response) => {
          if (response.body?.access_token) {
            this.onLoginSuccess();
          } else if (response.status === 460) {
            this._track.trackOtpVerified('login', 'email', false, 'invalid_code');
            this.error.set(this._translate.instant('invalidVerificationCode'));
          } else if (response.status === 470) {
            this._track.trackOtpVerified('login', 'email', false, 'no_account');
            this.error.set(this._translate.instant('noAccountFound'));
          }
          this.otpVerifying.set(false);
        },
        error: (err) => {
          this._track.trackOtpVerified(
            'login',
            'email',
            false,
            err?.error?.error ?? err?.status?.toString(),
          );
          this.error.set(err?.error?.error || this._translate.instant('verificationFailed'));
          this.otpVerifying.set(false);
        },
      });
  }

  private onLoginSuccess(): void {
    const returnUrl = this.returnUrl();
    if (returnUrl && returnUrl !== '/') {
      this._route.navigateByUrl(returnUrl, {replaceUrl: true});
    } else {
      // No meaningful deep link to return to — defer to the index route's
      // own currentLocationId/recentLocationIds fallback (app.routes.ts)
      // instead of re-deriving the same decision here.
      this._route.navigate(['/l/current/data'], {replaceUrl: true});
    }
  }

  private startResendCountdown(): void {
    this.otpResendCountdown.set(60);
    interval(1000)
      .pipe(takeWhile(() => this.otpResendCountdown() > 0))
      .subscribe(() => {
        this.otpResendCountdown.update((v) => v - 1);
      });
  }

  private initOauthFlow(): void {
    if (!this._isBrowser) return;

    this._track.trackOAuthStarted();

    const redirectUri = `${window.location.origin}/oauth-callback`;

    const oauthUrl = `https://api.yextapis.com/oauth2/authorize?client_id=cf3d0d3cb97546b7a9ab0acffa65f8ee&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&response_type=code&grant_type=authorization_code`;

    this.oauthWindow = window.open(oauthUrl, 'Yext login', 'width=600,height=700,left=200,top=100');
    window.addEventListener('message', this.receiveMessage, false);
  }

  private receiveMessage = (event: MessageEvent) => {
    if (!this._isBrowser) return;
    if (event.origin !== window.location.origin) {
      console.warn(`Message from untrusted origin ignored: ${event.origin}`);
      return;
    }

    if (event.data && event.data.type === 'oauth_code' && event.data.code) {
      const authCode = event.data.code;

      this._usersService
        .userLoginOauthPost({authorization_code: authCode})
        .pipe(
          first(),
          tap((response) => {
            this._track.trackLogin('login', 'oauth', true);
            this._auth.setAuthData(response.access_token, (response as any).account_type);
          }),
          switchMap((response) => this._locationsData.fetchLocations$.pipe(map(() => response))),
        )
        .subscribe({
          next: () => {
            this.onLoginSuccess();
          },
          error: (error) => {
            this._track.trackLogin(
              'login',
              'oauth',
              false,
              error?.error?.error ?? error?.status?.toString(),
            );
            this.error.set(error?.error?.error || this._translate.instant('loginError'));
          },
          complete: () => {
            if (this.oauthWindow) {
              this.oauthWindow.close();
            }
            window.removeEventListener('message', this.receiveMessage, false);
          },
        });
    }
  };

  clickEvent(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }
}
